import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import tilesService from '../services/tilesService';
import collectionsService from '../services/collectionsService';
import artisticObjectsService from '../../artisticobjects/services/artisticObjectsService';
import placesService from '../../artisticobjects/services/placesService';
import {Tile, TileType} from '../types/Tile';
import {handleError} from '../../../common/utils/errorHandling';
import AdminFormHeader from '../../../layout/admin/AdminFormHeader';
import {
  AdminButton,
  AdminForm,
  AdminFormSection,
  AdminInputDropdown,
  AdminInputNumber,
  AdminInputRow,
  AdminInputText,
  AdminRichTextArea
} from '../../../layout/admin';
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";
import {Collection} from '../types/Collection';
import {ArtisticObjectListing} from '../../artisticobjects/types/ArtisticObject';
import {PlaceListing} from '../../artisticobjects/types/Place';
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import FieldLanguageSwitcher from '../../../layout/admin/FieldLanguageSwitcher';
import {getValueWithFallback} from '../../../common/types/StringTranslations';

interface TileFormData {
  type: string;
  collectionId: string;
  artisticObjectId: string;
  placeId: string;
  path: string;
  width: number;
  height: number;
}

const TileForm: React.FC = () => {
  const {t, i18n} = useTranslation();
  const nameField = useTranslatableField();
  const tagField = useTranslatableField();
  const descriptionField = useTranslatableField();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [formData, setFormData] = useState<TileFormData>({
    type: '',
    collectionId: '',
    artisticObjectId: '',
    placeId: '',
    path: '',
    width: 2,
    height: 1
  });
  const [tile, setTile] = useState<Tile | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [places, setPlaces] = useState<PlaceListing[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  useEffect(() => {
    fetchReferenceData();
    if (isEditMode && id) {
      fetchTile(id);
    }
  }, [id, isEditMode]);

  const fetchReferenceData = async (): Promise<void> => {
    const [collectionsData, artisticObjectsData, placesData] = await Promise.all([
      collectionsService.getPublicCollections(),
      artisticObjectsService.getAllArtisticObjects(),
      placesService.getAllPlaces()
    ]);
    setCollections(collectionsData);
    setArtisticObjects(artisticObjectsData);
    setPlaces(placesData);
  };

  const fetchTile = async (tileId: string): Promise<void> => {
    await handleError(
      async () => {
        const tileData = await tilesService.getTile(tileId);
        setTile(tileData);
        if (tileData.name) {
          nameField.setTranslations(tileData.name);
        }
        if (tileData.tag) {
          tagField.setTranslations(tileData.tag);
        }
        if (tileData.description) {
          descriptionField.setTranslations(tileData.description);
        }
        setFormData({
          type: tileData.type || '',
          collectionId: tileData.collectionId || '',
          artisticObjectId: tileData.artisticObjectId || '',
          placeId: tileData.placeId || '',
          path: tileData.path || '',
          width: tileData.width || 2,
          height: tileData.height || 1
        });
      },
      t('admin.tiles.errors.fetching')
    );
  };

  const handleInputChange = (field: keyof TileFormData, value: string | number): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      const tileData: Omit<Tile, 'id'> | Tile = {
        ...(isEditMode && tile ? {id: tile.id} : {}),
        name: nameField.translations,
        type: formData.type as TileType,
        description: descriptionField.translations,
        tag: tagField.translations,
        collectionId: formData.type === TileType.COLLECTION ? formData.collectionId : undefined,
        artisticObjectId: formData.type === TileType.ARTISTIC_OBJECT ? formData.artisticObjectId : undefined,
        placeId: formData.type === TileType.PLACE ? formData.placeId : undefined,
        path: formData.type === TileType.PATH ? formData.path : undefined,
        width: formData.width,
        height: formData.height
      };

      if (isEditMode && tile) {
        await tilesService.updateTile(tileData as Tile);
      } else {
        await tilesService.createTile(tileData);
      }
      navigate('/tiles');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const typeOptions = [
    {label: t('admin.tiles.types.collection'), value: TileType.COLLECTION},
    {label: t('admin.tiles.types.artisticObject'), value: TileType.ARTISTIC_OBJECT},
    {label: t('admin.tiles.types.place'), value: TileType.PLACE},
    {label: t('admin.tiles.types.path'), value: TileType.PATH}
  ];

  const collectionOptions = collections.map(c => ({
    label: getValueWithFallback(c.title, i18n.language),
    value: c.id
  }));

  const artisticObjectOptions = artisticObjects.map(ao => ({
    label: ao.name,
    value: ao.id
  }));

  const placeOptions = places.map(p => ({
    label: p.name,
    value: p.id
  }));

  const renderTypeSpecificField = () => {
    switch (formData.type) {
      case TileType.COLLECTION:
        return (
          <AdminInputDropdown
            label={t('admin.tiles.fields.collection')}
            value={formData.collectionId}
            onChange={(e) => handleInputChange('collectionId', e.target.value)}
            options={collectionOptions}
            placeholder={t('admin.tiles.placeholders.selectCollection')}
            name="collectionId"
            id="collectionId"
            error={errors.collectionId}
          />
        );
      case TileType.ARTISTIC_OBJECT:
        return (
          <AdminInputDropdown
            label={t('admin.tiles.fields.artisticObject')}
            value={formData.artisticObjectId}
            onChange={(e) => handleInputChange('artisticObjectId', e.target.value)}
            options={artisticObjectOptions}
            placeholder={t('admin.tiles.placeholders.selectArtisticObject')}
            name="artisticObjectId"
            id="artisticObjectId"
            error={errors.artisticObjectId}
          />
        );
      case TileType.PLACE:
        return (
          <AdminInputDropdown
            label={t('admin.tiles.fields.place')}
            value={formData.placeId}
            onChange={(e) => handleInputChange('placeId', e.target.value)}
            options={placeOptions}
            placeholder={t('admin.tiles.placeholders.selectPlace')}
            name="placeId"
            id="placeId"
            error={errors.placeId}
          />
        );
      case TileType.PATH:
        return (
          <AdminInputText
            label={t('admin.tiles.fields.path')}
            value={formData.path}
            onChange={(e) => handleInputChange('path', e.target.value)}
            name="path"
            id="path"
            placeholder="/path/to/page"
            error={errors.path}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <AdminFormHeader
        title={isEditMode ? t('admin.tiles.title.edit') : t('admin.tiles.title.create')}
        subtitle={isEditMode ? t('admin.tiles.subtitle.edit') : t('admin.tiles.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={nameField.currentValue}
                onChange={(e) => nameField.setCurrentValue(e.target.value)}
                name="name"
                id="name"
                label={t('admin.common.fields.name')}
                required={true}
                error={errors.name}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={nameField.currentLanguage}
                    onLanguageChange={nameField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={nameField.translatedLanguages}
                  />
                }
              />

              <AdminInputDropdown
                label={t('admin.common.columns.type')}
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                options={typeOptions}
                placeholder={t('admin.tiles.placeholders.selectType')}
                name="type"
                id="type"
                required={true}
                error={errors.type}
              />

              {renderTypeSpecificField()}

              <AdminInputText
                value={tagField.currentValue}
                onChange={(e) => tagField.setCurrentValue(e.target.value)}
                name="tag"
                id="tag"
                label={t('admin.tiles.fields.tag')}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={tagField.currentLanguage}
                    onLanguageChange={tagField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={tagField.translatedLanguages}
                  />
                }
              />

              <AdminInputRow>
                <AdminInputNumber
                  label={t('admin.tiles.fields.width')}
                  value={formData.width}
                  onChange={(value) => handleInputChange('width', value)}
                  name="width"
                  id="width"
                  min={1}
                  max={4}
                  required={true}
                  error={errors.width}
                />
                <AdminInputNumber
                  label={t('admin.tiles.fields.height')}
                  value={formData.height}
                  onChange={(value) => handleInputChange('height', value)}
                  name="height"
                  id="height"
                  min={1}
                  max={4}
                  required={true}
                  error={errors.height}
                />
              </AdminInputRow>
            </AdminFormSection>
          </>
        }
        rightColumn={
          <AdminFormSection title={t('admin.common.sections.details')}>
            <AdminRichTextArea
              value={descriptionField.currentValue}
              onChange={descriptionField.setCurrentValue}
              name="description"
              id="description"
              label={t('admin.common.fields.description')}
              languageSwitcher={
                <FieldLanguageSwitcher
                  currentLanguage={descriptionField.currentLanguage}
                  onLanguageChange={descriptionField.setCurrentLanguage}
                  collapsed={true}
                  translatedLanguages={descriptionField.translatedLanguages}
                />
              }
            />
          </AdminFormSection>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate('/tiles')}
            />
            <AdminButton
              type="submit"
              label={tile ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default TileForm;

